import React, {
  useState,
  useEffect,
  useContext,
  FunctionComponent,
} from "react";
import { styled, Block, Modal, IModalProps } from "../../../seenspire-library";
import { get } from "lodash";
import { AppContext } from "../../state";
import { name } from "../../util";
import { ModalButtons } from "../../components/ModalButtons/ModalButtons";
import {
  createChannelPermission,
  deleteChannelPermission,
  getCuratorPermissions,
} from "../../backend/channelPermissions";
import { permissionDiff, ExistingPermission } from "./permissionDiff";

const Title = styled(Block)`
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  text-transform: uppercase;
  margin-bottom: 16px;
`;

const Catalog = styled(Block)`
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.colors.gray.light};
  border-radius: 6px;
`;

const CatalogRow = styled(Block)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray.lightest};
`;

const Thumb = styled.img`
  width: 56px;
  height: 36px;
  object-fit: cover;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.gray.lightest};
`;

const FeedTitle = styled.span`
  flex: 1;
`;

const AddButton = styled.button`
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  color: #fff;
  font-size: 18px;
  line-height: 1;
  background: ${({ theme, disabled }) =>
    disabled ? theme.colors.gray.base : theme.colors.cta.base};
`;

const ChipArea = styled(Block)`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
  min-height: 40px;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 4px;
  background: #eaf0ff;
  color: ${({ theme }) => theme.colors.cta.base};
  font-size: 13px;
`;

const ChipRemove = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 14px;
  color: inherit;
`;

const ErrorText = styled(Block)`
  color: ${({ theme }) => theme.colors.alert.base};
  margin-top: 12px;
`;

const AssignFeedsModal: FunctionComponent<
  IModalProps & {
    user: any;
    onSubmit: () => void;
    onCancel: () => void;
  }
> = ({ user, onSubmit, onCancel, ...props }) => {
  const { client, theme } = useContext(AppContext);
  const [feeds, setFeeds] = useState<any[]>([]);
  const [existing, setExisting] = useState<ExistingPermission[]>([]);
  const [selected, setSelected] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response: any = await client.ActiveChannels.getActiveChannels(
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        500
      );
      const accountFeeds = response.data || [];
      setFeeds(accountFeeds);
      const accountFeedIds = new Set(accountFeeds.map((f: any) => f.id));
      const perms: ExistingPermission[] = getCuratorPermissions(user)
        .filter((p) => accountFeedIds.has(p.active_channel.id))
        .map((p) => ({
          channelId: p.active_channel.id,
          permissionId: p.id,
        }));
      setExisting(perms);
      setSelected(perms.map((p) => p.channelId));
    } catch (e) {
      setError("Failed to load feeds. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const add = (channelId: number) =>
    setSelected((prev) =>
      prev.includes(channelId) ? prev : [...prev, channelId]
    );

  const remove = (channelId: number) =>
    setSelected((prev) => prev.filter((id) => id !== channelId));

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    const { toCreate, toDelete } = permissionDiff(existing, selected);
    try {
      await Promise.all([
        ...toCreate.map((channelId) =>
          createChannelPermission(channelId, user.id)
        ),
        ...toDelete.map((p) =>
          deleteChannelPermission(p.channelId, p.permissionId)
        ),
      ]);
      onSubmit();
    } catch (e) {
      setError("Could not save feed assignments. Please try again.");
      setSubmitting(false);
    }
  };

  const feedById = (id: number) => feeds.find((f) => f.id === id);

  const feedTitleById = (id: number): string | null =>
    feedById(id)?.title || null;

  return (
    <Modal onRequestClose={() => onCancel()} theme={theme} {...props}>
      <Title>Assign feeds to {name(user)}</Title>

      {loading ? (
        <Block padding={{ top: 20, bottom: 20 }}>Loading feeds…</Block>
      ) : (
        <>
          <Catalog>
            {feeds.map((feed) => {
              const isSelected = selected.includes(feed.id);
              return (
                <CatalogRow key={feed.id}>
                  <Thumb src={get(feed, "media[0].thumb.url") || ""} alt="" />
                  <FeedTitle>{feed.title}</FeedTitle>
                  <AddButton disabled={isSelected} onClick={() => add(feed.id)}>
                    +
                  </AddButton>
                </CatalogRow>
              );
            })}
          </Catalog>

          <ChipArea>
            {selected.map((id) => (
              <Chip key={id}>
                {feedTitleById(id) || `#${id}`}
                <ChipRemove onClick={() => remove(id)}>×</ChipRemove>
              </Chip>
            ))}
          </ChipArea>
        </>
      )}

      {error && <ErrorText>{error}</ErrorText>}

      <ModalButtons
        onCancel={onCancel}
        onSubmit={submit}
        cancel="Cancel"
        submit="Assign feeds"
        disableSubmit={submitting || loading}
      />
    </Modal>
  );
};

export { AssignFeedsModal };
